import React, { useMemo, useRef, useState } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import { useTheme, alpha } from '@mui/material/styles';
import ReactMarkdown from 'react-markdown';
import { RefreshCw } from 'react-feather';
import NRRaiseAISBOMTable from '../../../pages/NRSBOMTable/NRRaiseAISBOMTable';
import NRRiseAIVulnerabilitiesTable from '../../NRVulnerabilitiesTable/NRRiseAIVulnerabilitiesTable';
import { toVexStatusFromRest, getButtonVariant, getButtonColor } from '../utils/messageUtils';
import { truncateToDecimal } from '../../../utils/utils';
import ActionsContainer from './ActionsContainer';

export default function MessageItem({ 
  message, 
  onRetry, 
  showHeader,
  feedback,
  onFeedbackSubmit,
  onFeedbackDelete,
  sessionId,
  eventId,
  onButtonClick
}) {
  const theme = useTheme();
  const vulnerabilitiesTableRef = useRef(null);
  const [buttonsDisabled, setButtonsDisabled] = useState(false);

  const nrvulnerabilities = useMemo(() => {
    const vulns = message?.vulnerabilities;
    if (!Array.isArray(vulns) || vulns.length === 0) return [];
    return vulns.map(v => ({
      // Map AI response fields -> NRVulnerabilitiesTable expected row shape
      cve: v?.vulnerabilityName || v?.cve || '',
      severity: v?.severity,
      name: v?.name,
      vendor: v?.vendor,
      version: v?.version,
      epssScore: v?.epssScore,
      epssPercentile: v?.epssPercentile,
      cvssScore: truncateToDecimal(v?.cvssScore, 1),
      isReachable: v?.reachable,
      maturity: v?.maturity,
      attackVector: v?.attackVector,
      attackComplexity: v?.attackComplexity,
      inKnownExploitedVulnerabilities: v?.inKnownExploitedVulnerabilities,
      inBotnets: v?.inBotnets,
      inKnownAttacks: v?.inKnownAttacks,
      inRansomware: v?.inRansomware,
      inTheWild: v?.inTheWild,
      inThreatActors: v?.inThreatActors,
      filePath: v?.filePath,
      exploits: v?.exploits,
      correlations: typeof v?.exploits === 'number' ? v.exploits : 0,
      hasFixVersion: v?.hasFixVersion,
      fixVersions: v?.fixVersions,
      vulnerabilityId: v?.vulnerabilityId,
      createdDatetime: v?.createdDatetime,
      updatedDatetime: v?.updatedDatetime,
      kevDueDate: v?.kevDueDate,
      currentRemediation: v?.vexStatus ? { status: toVexStatusFromRest(v.vexStatus) } : undefined
    }));
  }, [message?.vulnerabilities]);

  const isAssistantMessage = message.role === 'assistant';
  const hasContent = message.content || (message.components && message.components.length > 0) || (message.vulnerabilities && message.vulnerabilities.length > 0) || (message.actionButtons && message.actionButtons.length > 0);

  const handleButtonClick = (value) => {
    setButtonsDisabled(true);
    if (onButtonClick) {
      onButtonClick(value);
    }
  };

  return (
    <Box>
      {/* Only render message box if there's content, components, vulnerabilities, or action buttons */}
      {(message.content ||
        (message.components && message.components.length > 0) ||
        (message.vulnerabilities && message.vulnerabilities.length > 0) ||
        (message.actionButtons && message.actionButtons.length > 0)) && (
        <Box
          sx={{
            display: 'flex',
            justifyContent: message.role === 'user' ? 'flex-end' : 'flex-start',
            mb: 1,
            ml: message.role === 'assistant' ? 3 : 0,
            width: '100%'
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: message.role === 'user' ? 'auto' : '100%',
              maxWidth: message.role === 'user' ? '600px' : '100%',
              padding: message.role === 'user' ? theme.spacing(3) : 0,
              mb: message.role === 'user' ? theme.spacing(3) : 0,
              backgroundColor: message.role === 'user' ? theme.palette.neutralMessageBackground : 'transparent',
              color: theme.palette.text.primary,
              borderRadius: message.role === 'user'
                ? `${theme.shape.borderRadius * 4}px 0 ${theme.shape.borderRadius * 4}px ${theme.shape.borderRadius * 4}px`
                : 0,
              border: 'none'
            }}
          >
            {message.content && (
              <>
                {message.role === 'assistant' ? (
                  <Box
                    sx={{
                      ...theme.typography.body1,
                      '& p': {
                        margin: 0,
                        marginBottom: 1,
                        '&:last-child': {
                          marginBottom: 0
                        }
                      },
                      '& ul, & ol': {
                        margin: 0,
                        marginBottom: 1,
                        paddingLeft: 2,
                        '&:last-child': {
                          marginBottom: 0
                        }
                      },
                      '& h1, & h2, & h3, & h4, & h5, & h6': {
                        margin: 0,
                        marginTop: 1,
                        marginBottom: 0.5,
                        fontWeight: 600,
                        '&:first-of-type': {
                          marginTop: 0
                        }
                      },
                      '& code': {
                        backgroundColor: theme.palette.action.hover,
                        padding: theme.spacing(0.25, 0.5),
                        borderRadius: theme.shape.borderRadius,
                        fontFamily: theme.typography.fontFamilyMonospace || 'monospace',
                        fontSize: theme.typography.body2.fontSize
                      },
                      '& pre': {
                        backgroundColor: theme.palette.action.hover,
                        padding: theme.spacing(1),
                        borderRadius: theme.shape.borderRadius,
                        overflow: 'auto',
                        margin: 0,
                        marginBottom: 1,
                        '&:last-child': {
                          marginBottom: 0
                        },
                        '& code': {
                          backgroundColor: 'transparent',
                          padding: 0
                        }
                      },
                      '& blockquote': {
                        borderLeft: `3px solid ${theme.palette.divider}`,
                        paddingLeft: theme.spacing(1),
                        margin: 0,
                        marginBottom: 1,
                        fontStyle: 'italic',
                        '&:last-child': {
                          marginBottom: 0
                        }
                      },
                      '& table': {
                        borderCollapse: 'collapse',
                        width: '100%',
                        marginBottom: 1,
                        '&:last-child': {
                          marginBottom: 0
                        }
                      },
                      '& th, & td': {
                        border: `1px solid ${theme.palette.divider}`,
                        padding: theme.spacing(0.5, 1),
                        textAlign: 'left'
                      },
                      '& th': {
                        backgroundColor: theme.palette.action.hover,
                        fontWeight: 600
                      },
                      '& a': {
                        color: theme.palette.primary.main,
                        textDecoration: 'none',
                        '&:hover': {
                          textDecoration: 'underline'
                        }
                      },
                      '& hr': {
                        border: 'none',
                        borderTop: `1px solid ${theme.palette.divider}`,
                        margin: theme.spacing(1, 0)
                      }
                    }}
                  >
                    <ReactMarkdown>{message.content}</ReactMarkdown>
                  </Box>
                ) : (
                  <Typography
                    variant='body1'
                    sx={{
                      whiteSpace: 'pre-wrap'
                    }}
                  >
                    {message.content}
                  </Typography>
                )}
              </>
            )}
            {message.components && message.components.length > 0 && <NRRaiseAISBOMTable components={message.components} />}
            {message.vulnerabilities && message.vulnerabilities.length > 0 && (
              <Box
                sx={{
                  maxWidth: '100%',
                  overflowX: 'auto',
                  WebkitOverflowScrolling: 'touch',
                  // Ensure the inner table/grid is allowed to be wider than the message bubble if needed
                  '& > *': { minWidth: 'max-content' }
                }}
              >
                <NRRiseAIVulnerabilitiesTable
                  vulnerabilities={nrvulnerabilities}
                  tableRef={vulnerabilitiesTableRef}
                  isLoading={false}
                  tableStorageId="RiseAIChatVulnerabilitiesBasicTable"
                  disabledProviders={[]}
                />
              </Box>
            )}
            {message.actionButtons && message.actionButtons.length > 0 && (
              <Box sx={{ mt: 2, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {message.actionButtons.map((button, index) => (
                  <Button
                    key={index}
                    variant={getButtonVariant(button.class)}
                    color={getButtonColor(button.class)}
                    onClick={() => handleButtonClick(button.value)}
                    disabled={buttonsDisabled}
                    sx={{ textTransform: 'none' }}
                  >
                    {button.label}
                  </Button>
                ))}
              </Box>
            )}
            {message.retryable && onRetry && (
              <Box sx={{ mt: 1.5, display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Button
                  variant="outlined"
                  size="medium"
                  startIcon={<RefreshCw size={14} />}
                  onClick={onRetry}
                  sx={{
                    ...theme.typography.caption,
                    alignSelf: 'flex-start',
                    textTransform: 'unset',
                    whiteSpace: 'nowrap',
                    py: 1,
                    px: 1.5,
                    minWidth: 'auto',
                    borderColor: theme.palette.riseAiPurple,
                    color: theme.palette.riseAiPurple,
                    '&:hover': {
                      borderColor: theme.palette.riseAiPurple,
                      backgroundColor: alpha(theme.palette.riseAiPurple, 0.1)
                    }
                  }}
                >
                  Retry with previous message
                </Button>
              </Box>
            )}

            {isAssistantMessage && hasContent && eventId && (
              <ActionsContainer
                message={message}
                feedback={feedback}
                onFeedbackSubmit={onFeedbackSubmit}
                onFeedbackDelete={onFeedbackDelete}
                sessionId={sessionId}
                eventId={eventId}
              />
            )}
          </Paper>
        </Box>
      )}
    </Box>
  );
}
